/**
 * Modal dialog keyboard behaviour: focus in, focus out, and Escape.
 *
 * A dialog that leaves focus behind it is not really modal — a keyboard or screen
 * reader user tabs straight into the content the backdrop is meant to have made
 * inert, and lands somewhere unrelated when the dialog closes. The three pieces
 * that fix that (focus the first control on open, cycle Tab within the panel,
 * restore focus to the opener on close) always travel together, so they live here
 * rather than being re-derived per dialog.
 *
 * Extracted from the create-passkey prompt when the second-account prompt needed
 * the same behaviour; the logic is unchanged from that original.
 */

import { useEffect, type RefObject } from 'react';

/** The focusable elements the trap cycles through, in DOM order. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Trap focus inside a dialog panel and close it on Escape.
 *
 * @param panelRef The dialog panel. Focus is confined to its focusable children.
 * @param onClose Called on Escape. The caller decides what closing means.
 */
export function useDialogTrap(panelRef: RefObject<HTMLElement | null>, onClose: () => void): void {
  // Focus the first control on open and restore focus to the opener on close, so a
  // keyboard user lands back where they were rather than at the top of the document.
  useEffect(() => {
    const opener = (document.activeElement as HTMLElement) ?? null;
    const panel = panelRef.current;
    if (panel) {
      panel.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    }
    return () => opener?.focus();
    // The panel ref is stable for the dialog's lifetime; this must run once on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape closes, and Tab is trapped within the panel so focus cannot reach the
  // inert content behind the backdrop.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [panelRef, onClose]);
}
